// First-run tutorial (about a minute): a card at the bottom of the screen gives a new runner four one-line steps:
// move and fight, take a car, buy ammo at Hachi's, open the menu. Everything else is left to discover.
// Client-only: it reads the snapshot and character to notice when a step is done, and points at places with the
// normal HUD objective marker. It never pauses the world. Finishing or skipping it is remembered in this browser;
// Settings > "Replay the tutorial" starts it again.
import { WORLD, AMMO_TYPES } from '../../common/src/data';
import { TILE } from '../../common/src/map';
import type { Character } from '../../common/src/rules';
import type { PlayerSnap, Snapshot } from '../../common/src/protocol';
import type { UI } from './ui';

const KEY = 'nk.tutorial';
const HACHI = WORLD.pois.find(p => p.kind === 'weapons')!;
const HACHI_POS = { x: HACHI.x * TILE, y: HACHI.y * TILE }; // the counter: shops answer [USE] within 40 px of this point

export interface TutState { s: Snapshot; me: PlayerSnap; char: Character; x: number; y: number; touch: boolean }
interface Step {
  title: string;
  text: (t: TutState) => string;
  done: (t: TutState) => boolean;
  /** world point for the HUD objective marker */
  target?: (t: TutState) => { x: number; y: number } | null;
}
const ammoTotal = (c: Character) => AMMO_TYPES.reduce((s, a) => s + (c.ammo[a] ?? 0), 0);

export class Tutorial {
  step = -1;
  private el: HTMLDivElement;
  private start0 = { x: 0, y: 0, ammo: 0 };
  private html = '';
  private steps: Step[];

  constructor(private ui: UI) {
    this.el = document.createElement('div'); this.el.id = 'tut'; document.body.appendChild(this.el);
    this.el.addEventListener('click', e => {
      if ((e.target as HTMLElement).closest('button')?.dataset.tut === 'skip') this.finish();
    });
    const key = (t: TutState, kb: string, tap: string) => (t.touch ? tap : kb);
    this.steps = [
      { title: 'Move', text: t => `${key(t, '<b>WASD</b> to move, mouse to aim, click to attack', 'Left thumb moves; right thumb aims, and pushing past the red ring attacks')}. Walk a few steps.`,
        done: t => Math.hypot(t.x - this.start0.x, t.y - this.start0.y) > 48 },
      { title: 'Get a ride', text: t => `Walk up to a car and press ${key(t, '<b>E</b>', '<b>USE</b>, then point the left thumb where you want to drive')} to take it.`,
        done: t => !!t.me.v, target: t => this.nearestCar(t) },
      { title: 'Buy ammo', text: t => `Hachi's Hardware (marker) sells ammo. Stand at the counter, press ${key(t, '<b>E</b>', '<b>USE</b>')} and buy a pack.`,
        done: t => ammoTotal(t.char) > this.start0.ammo, target: () => HACHI_POS },
      { title: 'Menus', text: t => `Open the menu with ${key(t, '<b>Tab</b>', '<b>MENU</b>')}. Take jobs at the fixer kiosks (diamonds on the map). The city keeps moving.`,
        done: () => this.ui.open },
    ];
  }

  /** Story mode teaches the same things, so finishing it marks the tutorial seen in this browser. */
  static markSeen() { try { localStorage.setItem(KEY, 'done'); } catch { /* storage blocked */ } }
  static seen() { try { return localStorage.getItem(KEY) === 'done'; } catch { return false; } }
  get active() { return this.step >= 0; }

  /** called when the tutorial is finished or skipped */
  onDone: () => void = () => {};
  start() { this.step = 0; this.pendingRebase = true; this.html = ''; }
  finish() {
    const was = this.step >= 0;
    this.step = -1; this.el.style.display = 'none'; this.html = ''; this.fitPanel();
    try { localStorage.setItem(KEY, 'done'); } catch { /* storage blocked: it may show again next time */ }
    if (was) this.onDone();
  }
  private pendingRebase = true;
  private rebase(t: TutState) { this.start0 = { x: t.x, y: t.y, ammo: ammoTotal(t.char) }; this.pendingRebase = false; }
  /** Keep the open menu above the card, so the card never covers the buttons it tells the player to press. */
  private fitPanel() {
    const pn = this.ui.panel;
    if (document.body.classList.contains('touch')) { // phones size the menu by its edges (index.html), so lift its bottom edge
      const b = this.step >= 0 && this.ui.open ? `${Math.round(innerHeight - this.el.getBoundingClientRect().top + 8)}px` : '';
      if (pn.style.bottom !== b) pn.style.bottom = b;
      return;
    }
    const base = Number(pn.dataset.maxh) || 0; if (!base) return;
    let max = base;
    if (this.step >= 0 && this.ui.open) max = Math.max(120, Math.min(base, this.el.getBoundingClientRect().top - pn.getBoundingClientRect().top - 8));
    const v = `${Math.round(max)}px`; if (pn.style.maxHeight !== v) pn.style.maxHeight = v;
  }
  private nearestCar(t: TutState) {
    let best: { x: number; y: number } | null = null, bd = Infinity;
    for (const v of t.s.v) {
      if (v.f & 1 || v.o.some(o => o > 0)) continue; // no police cars, nobody else's ride
      const d = Math.hypot(v.x - t.x, v.y - t.y); if (d < bd) { bd = d; best = { x: v.x, y: v.y }; }
    }
    return best;
  }

  /** Call every frame. Returns the objective marker to show, or null. */
  update(t: TutState): { text: string; x: number; y: number } | null {
    if (this.step < 0) return null;
    if (this.pendingRebase) this.rebase(t);
    const st = this.steps[this.step];
    if (st.done(t)) {
      this.pendingRebase = true;
      if (++this.step >= this.steps.length) this.finish();
      return null;
    }
    const html = `<div class="t"><b>${st.title}</b><span class="n">${this.step + 1}/${this.steps.length}</span></div><div>${st.text(t)}</div>
      <div class="b"><button data-tut="skip" class="dim">Skip tutorial</button></div>`;
    if (html !== this.html) { this.html = html; this.el.innerHTML = html; this.el.style.display = 'block'; }
    this.fitPanel();
    const p = st.target?.(t);
    return p ? { text: st.title, ...p } : null;
  }
}
